import { MainLayoutComponent } from './layouts/main-layout.component';
import { Routes, RouterModule } from '@angular/router';
import { NgModule } from '@angular/core';
import { unsavedDraftGuard } from '@mtm/conversation-ui-angular';

import { AuthGuard } from './shared/guards/auth.guard';
import { MtmAdminGuard } from './shared/guards/mtm-admin.guard';
import { authGuestGuard } from './shared/guards/auth-guest.guard';
import { guestConfinementGuard, outsideAppShellMatch } from './shared/guards/guest-confinement.guard';

import { ContentUnavailableComponent } from './errors/content-unavailable/content-unavailable.component';
import { canActivateShortener } from './shared/guards/shortener.guard';
import { CanAccessTask } from './shared/guards/can-access-task';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'entry',
    pathMatch: 'full',
  },
  {
    path: 'mtm-tcp',
    loadChildren: () => import('./sub-modules/tcp-module/tcp-module').then((m) => m.TCPModule),
  },
  {
    path: 'entry',
    loadChildren: () => import('./sub-modules/entry-module/entry-module').then((m) => m.EntryModule),
  },
  {
    path: 'admin',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard, AuthGuard, MtmAdminGuard],
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./sub-modules/admin-module/admin-module').then((m) => m.AdminModule),
  },
  {
    path: 'account',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard, AuthGuard],
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./sub-modules/account-module/account-module').then((m) => m.AccountModule),
  },
  {
    path: 'workspace',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard],
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./workspace/workspace.module').then((m) => m.WorkspaceModule),
    // loadChildren: './workspace/workspace.module#WorkspaceModule'
  },
  {
    path: 'projects',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard, AuthGuard],
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./sub-modules/projects-module/projects-module').then((m) => m.ProjectsModule),
  },
  {
    path: 'short/:slug',
    component: MainLayoutComponent,
    canDeactivate: [unsavedDraftGuard],
    canActivate: [canActivateShortener],
  },
  {
    path: 'projects/:projectId',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard, AuthGuard],
    canDeactivate: [unsavedDraftGuard],
    data: {
      showNav: true,
    },
    loadChildren: () => import('./sub-modules/project-modules/project-RootModule').then((m) => m.ProjectRootModule),
  },
  {
    path: 'review/:reviewLinkId',
    component: MainLayoutComponent,
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./sub-modules/review-link/review-link.module').then((m) => m.ReviewLinkModule),
    data: {
      publicReviewLink: true,
    },
  },
  {
    path: 'share/:shareLinkId',
    component: MainLayoutComponent,
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./sub-modules/share-link/share-link.module').then((m) => m.ShareLinkModule),
    data: {
      publicShareLink: true,
    },
  },
  {
    // Drive shared-surface viewer — the guest side of a Drive share link. Deliberately
    // NOT under `MainLayoutComponent`: the library draws its own header and the page is
    // a read-only guest surface, so the project shell would only duplicate chrome.
    //
    // `loadChildren`, not `loadComponent`, because the media-studio providers the
    // viewer needs must not be named from this eagerly-loaded file — see the routes
    // file for the whole story. The address itself is `provideDrive({ surfaceViewerUrl })`
    // in `drive.providers.ts`; keep the two in step. Unrelated to `share/:shareLinkId`
    // below, which is the legacy sharelink feature. The `:id` segment is declared by
    // the child route, not here — see `drive-share.routes.ts`.
    path: 's',
    canActivate: [authGuestGuard],
    loadChildren: () => import('./sub-modules/drive-share/drive-share.routes').then((m) => m.driveShareRoutes),
  },
  {
    path: 'tasks',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard, AuthGuard, CanAccessTask],
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./sub-modules/task-v2-module/task-v2.module').then((m) => m.TaskV2Module),
    // @TODO: remove legacy task module
    // loadChildren: () => import('./sub-modules/task-module/task.module').then((m) => m.TaskModule),
  },
  {
    path: 'workspaces',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard, AuthGuard],
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./workspace-v2/campaign-workspace.module').then((m) => m.CampaignWorkspaceModule),
  },
  {
    path: 'my-space',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard, AuthGuard],
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./layouts/my-space/my-space.routes').then((m) => m.MY_SPACE_ROUTES),
  },
  {
    // A share-link guest lands here from a link that no longer exists (`GuestShareLogoutService`), and
    // an anonymous visitor from the sign-in screen when their share link has expired (`mtm-auth`).
    // The route below would bounce both to sign-in (`guestConfinementGuard`, `AuthGuard`), so they
    // get the same page without the app shell.
    path: 'unavailable',
    canMatch: [outsideAppShellMatch],
    component: ContentUnavailableComponent,
  },
  {
    path: 'unavailable',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard, AuthGuard],
    children: [{ path: '', component: ContentUnavailableComponent }],
  },
  {
    path: 'dashboard',
    component: MainLayoutComponent,
    canActivate: [guestConfinementGuard],
    canDeactivate: [unsavedDraftGuard],
    loadChildren: () => import('./workspace-v2/analytics/reporting-v2/reporting.module').then((m) => m.ReportingModule),
  },
  {
    path: 'auth/process',
    redirectTo: 'entry/auth/process',
  },
  {
    path: 'sso/callback',
    redirectTo: 'entry/sso/callback',
  },
  {
    path: '**',
    redirectTo: 'unavailable',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
